/**
 * Flow categories — user-editable list of grouping labels.
 *
 * v1 scope: pure UI grouping. Categories are NOT stored on the flow
 * itself (yet). They drive:
 *   - the dropdown in NewFlowModal (so the user picks one at create-time)
 *   - the filter pills on MarketplacePage (templates declare a category)
 *
 * Persisted in localStorage at `ipaas:categories`. Cross-tab sync via the
 * `storage` event + a custom in-tab event so Settings edits update the
 * dropdown / pills without a reload.
 *
 * v2 will add:
 *   - backend persistence (_categories.json next to runtimes)
 *   - Flow.category as a first-class field
 *   - per-category filter pills on the FlowList master-detail
 *   - Marketplace pulls templates' categories from this list
 *
 * If you find yourself reading from a stored Flow.category, rip out
 * this hook and move the source of truth server-side.
 */
import { useEffect, useState } from 'react';

export const CATEGORIES_KEY = 'pipebase:categories';
const CHANGE_EVENT = 'pipebase:categories-changed';

/** Default catalogue from the Claude design (platform-screens-1.jsx). */
export const DEFAULT_CATEGORIES: string[] = [
  'Order-to-Cash',
  'Finance',
  'Analytics',
  'EDI',
  'People',
  'Compliance',
  'MDM',
  'Operations',
  'Other',
];

const MAX_CATEGORIES = 32;
const MAX_LABEL_LEN = 40;
const NAME_RE = /^[A-Za-z0-9][A-Za-z0-9 \-/&+()._]{0,38}[A-Za-z0-9)]?$/;

function readPersisted(): string[] | null {
  try {
    const raw = localStorage.getItem(CATEGORIES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const cleaned = parsed
      .map((s) => (typeof s === 'string' ? s.trim().slice(0, MAX_LABEL_LEN) : ''))
      .filter((s) => s.length > 0);
    // De-dup case-insensitively while preserving the first-seen casing.
    const seen = new Set<string>();
    const out: string[] = [];
    for (const c of cleaned) {
      const k = c.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(c);
      if (out.length >= MAX_CATEGORIES) break;
    }
    return out;
  } catch {
    return null;
  }
}

export function getCategories(): string[] {
  return readPersisted() ?? [...DEFAULT_CATEGORIES];
}

function writePersisted(list: string[]): void {
  try {
    localStorage.setItem(CATEGORIES_KEY, JSON.stringify(list));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    /* localStorage unavailable — silent no-op */
  }
}

export function isValidCategoryName(raw: string): boolean {
  if (typeof raw !== 'string') return false;
  const t = raw.trim();
  if (t.length === 0 || t.length > MAX_LABEL_LEN) return false;
  return NAME_RE.test(t);
}

/**
 * React subscriber. Returns the current list + setters that mutate
 * localStorage. Setters reject empty / overlong / duplicate names.
 */
export interface UseCategories {
  categories: string[];
  add: (name: string) => { ok: boolean; error?: string };
  rename: (oldName: string, newName: string) => { ok: boolean; error?: string };
  remove: (name: string) => void;
  reset: () => void;
}

export function useCategories(): UseCategories {
  const [categories, setCategories] = useState<string[]>(getCategories);

  useEffect(() => {
    const sync = () => setCategories(getCategories());
    window.addEventListener(CHANGE_EVENT, sync);
    const onStorage = (e: StorageEvent) => {
      if (e.key === CATEGORIES_KEY) sync();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const add = (name: string): { ok: boolean; error?: string } => {
    const t = name.trim();
    if (!isValidCategoryName(t)) {
      return { ok: false, error: 'Use 1-40 letters, digits, spaces, or - / & + ( ) . _' };
    }
    const cur = getCategories();
    if (cur.some((c) => c.toLowerCase() === t.toLowerCase())) {
      return { ok: false, error: 'A category with that name already exists' };
    }
    if (cur.length >= MAX_CATEGORIES) {
      return { ok: false, error: `Up to ${MAX_CATEGORIES} categories supported` };
    }
    writePersisted([...cur, t]);
    return { ok: true };
  };

  const rename = (oldName: string, newName: string): { ok: boolean; error?: string } => {
    const t = newName.trim();
    if (!isValidCategoryName(t)) {
      return { ok: false, error: 'Use 1-40 letters, digits, spaces, or - / & + ( ) . _' };
    }
    const cur = getCategories();
    const idx = cur.findIndex((c) => c.toLowerCase() === oldName.toLowerCase());
    if (idx < 0) return { ok: false, error: 'Category not found' };
    if (
      t.toLowerCase() !== oldName.toLowerCase() &&
      cur.some((c) => c.toLowerCase() === t.toLowerCase())
    ) {
      return { ok: false, error: 'A category with that name already exists' };
    }
    const next = [...cur];
    next[idx] = t;
    writePersisted(next);
    return { ok: true };
  };

  const remove = (name: string): void => {
    const cur = getCategories();
    const next = cur.filter((c) => c.toLowerCase() !== name.toLowerCase());
    writePersisted(next);
  };

  const reset = (): void => {
    writePersisted([...DEFAULT_CATEGORIES]);
  };

  return { categories, add, rename, remove, reset };
}
